import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook to block click events on interactive elements that were used to initiate drag operations
 * @param isDragging Whether a drag is currently in progress
 * @param dragStartTime Timestamp when the drag started
 */
export function useDragClickBlocker(isDragging: boolean, dragStartTime: number) {
  const pointerDownTargetRef = useRef<EventTarget | null>(null);
  const pointerDownPosRef = useRef<{ x: number; y: number } | null>(null);
  const movedRef = useRef<boolean>(false);
  const throttleTimeoutRef = useRef<number | null>(null);

  // Throttled pointer move handler for better performance
  const throttledPointerMove = useCallback((e: PointerEvent) => {
    if (throttleTimeoutRef.current) {
      return;
    }
    
    throttleTimeoutRef.current = window.setTimeout(() => {
      throttleTimeoutRef.current = null;
    }, 16);

    if (pointerDownPosRef.current) {
      const dx = e.clientX - pointerDownPosRef.current.x;
      const dy = e.clientY - pointerDownPosRef.current.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        movedRef.current = true;
      }
    }
  }, []);

  useEffect(() => {
    function handlePointerDown(e: PointerEvent) {
      const target = e.target as HTMLElement;
      
      pointerDownTargetRef.current = null;
      pointerDownPosRef.current = { x: e.clientX, y: e.clientY };
      movedRef.current = false;
      
      if (!target.closest('.grid-layout-drag-cancel')) {
        const interactiveEl = target.closest('a, button, [onclick], .order-number-span, [role="button"]');
        if (interactiveEl) {
          pointerDownTargetRef.current = interactiveEl;
        }
      }
    }

    function handlePointerUp() {
      setTimeout(() => {
        pointerDownTargetRef.current = null;
        pointerDownPosRef.current = null;
        movedRef.current = false;
      }, 200);
    }

    function handleGlobalClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      
      if (target.closest('.grid-layout-drag-cancel')) {
        return;
      }
      
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
      
      const clickedEl = target.closest('a, button, [onclick], .order-number-span, [role="button"]');
      if (clickedEl === pointerDownTargetRef.current && movedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        pointerDownTargetRef.current = null;
        movedRef.current = false;
        
        return false;
      }
    }

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('pointermove', throttledPointerMove, true);
    document.addEventListener('pointerup', handlePointerUp, true);
    document.addEventListener('click', handleGlobalClick, true);

    return () => {
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
        throttleTimeoutRef.current = null;
      }
      
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('pointermove', throttledPointerMove, true);
      document.removeEventListener('pointerup', handlePointerUp, true);
      document.removeEventListener('click', handleGlobalClick, true);
    };
  }, [isDragging, throttledPointerMove]);
}